import { mapActions, mapGetters, mapMutations } from 'vuex'
import UiCounter from '@/components/@ui/counter/index.vue'
import { buildBasketPayloadProducts } from '@/helpers'

export default {
  name: 'select-gift',
  components: {
    'ui-counter': UiCounter,
  },
  data() {
    return {
      draft: {},
    }
  },
  computed: {
    ...mapGetters({
      availableSales: 'gifts/availableSales',
      storedSelected: 'gifts/selectedGifts',
      basket: 'basket/basket',
    }),
    canConfirm() {
      return this.availableSales.every(
        (sale) => this.selectedInSale(sale.saleId) === sale.maxCount,
      )
    },
  },
  watch: {
    availableSales: {
      immediate: true,
      handler(next) {
        this.initDraft()
        if (!next || next.length === 0) {
          this.changeSelectGiftPopup(false)
        }
      },
    },
  },
  methods: {
    ...mapActions({
      applySelectedGifts: 'gifts/APPLY_SELECTED_GIFTS',
      fetchPromotion: 'basket/FETCH_PROMOTION',
    }),
    ...mapMutations({
      changeSelectGiftPopup: 'popups/CHANGE_SELECT_GIFT_POPUP',
    }),
    initDraft() {
      const next = {}
      this.availableSales.forEach((sale) => {
        next[sale.saleId] = (this.storedSelected || [])
          .filter((item) => item.saleId === sale.saleId)
          .map((item) => ({ ...item }))
      })
      this.draft = next
    },
    selectedInSale(saleId) {
      const list = this.draft[saleId] || []
      return list.reduce((sum, item) => sum + item.count, 0)
    },
    isVariantSelected(saleId, variant) {
      const list = this.draft[saleId] || []
      return list.some((item) => item.productPriceId === variant.productPriceId)
    },
    variantCount(saleId, variant) {
      const list = this.draft[saleId] || []
      const found = list.find((item) => item.productPriceId === variant.productPriceId)
      return found ? found.count : 0
    },
    variantLeft(variant) {
      if (variant.maxQuantity === null || variant.maxQuantity === undefined) {
        return null
      }
      return Math.max(0, variant.maxQuantity - (variant.givenQuantity || 0))
    },
    variantEffectiveMax(sale, variant) {
      const left = this.variantLeft(variant)
      return left === null ? sale.maxCount : Math.min(sale.maxCount, left)
    },
    showVariantCounter(sale, variant) {
      return this.variantEffectiveMax(sale, variant) > 1
    },
    variantMax(sale, variant) {
      const used = this.selectedInSale(sale.saleId)
      const current = this.variantCount(sale.saleId, variant)
      const remaining = sale.maxCount - used + current
      return Math.min(remaining, this.variantEffectiveMax(sale, variant))
    },
    setVariantCount(sale, variant, nextCount) {
      const list = this.draft[sale.saleId] ? [...this.draft[sale.saleId]] : []
      const idx = list.findIndex((item) => item.productPriceId === variant.productPriceId)

      if (nextCount <= 0) {
        if (idx >= 0) list.splice(idx, 1)
      } else if (idx >= 0) {
        list[idx] = { ...list[idx], count: nextCount }
      } else {
        list.push({
          saleId: sale.saleId,
          productPriceId: variant.productPriceId,
          count: nextCount,
        })
      }

      this.$set(this.draft, sale.saleId, list)
    },
    toggleVariant(sale, variant) {
      const current = this.variantCount(sale.saleId, variant)
      if (current > 0) {
        this.setVariantCount(sale, variant, 0)
        return
      }
      const used = this.selectedInSale(sale.saleId)
      if (used >= sale.maxCount) return
      this.setVariantCount(sale, variant, 1)
    },
    async confirm() {
      const flat = Object.keys(this.draft).reduce((acc, saleId) => {
        return acc.concat(this.draft[saleId] || [])
      }, [])
      const reconciled = await this.applySelectedGifts(flat)
      this.changeSelectGiftPopup(false)

      const giftItems = (reconciled || []).map((g) => ({
        product_price_id: g.productPriceId,
        count: g.count,
        is_free: true,
        sale_id: g.saleId,
      }))

      // Drop the previously chosen gifts from the basket before building the
      // payload so the freshly reconciled selection is the one that gets sent.
      const basketWithoutChosenGifts = (this.basket || []).filter((p) => !p.isFree)

      try {
        await this.fetchPromotion({
          products: buildBasketPayloadProducts(basketWithoutChosenGifts, giftItems),
        })
      } catch (e) {
        console.error('Re-sync basket after gift apply failed', e)
      }
    },
    close() {
      this.changeSelectGiftPopup(false)
    },
    variantTitle(variant) {
      if (variant.value && variant.dimension) {
        return `${variant.value} ${variant.dimension}`
      }
      if (variant.value) return `${variant.value}`
      return ''
    },
  },
}
